import { mercatorUtils } from '@fe/maplibre-extensions';
import { CustomLayerInterface, CustomRenderMethodInput, Map as MaplibreMap } from 'maplibre-gl';
import fragmentSource from './circle.fragment.glsl?raw';
import vertexSource from './circle.vert.glsl?raw';

type ZoomExpression = number | ((zoom: number) => number);

export class CircleLayer implements CustomLayerInterface {
    readonly type = 'custom';
    readonly renderingMode?: '2d';
    private shaderMap = new Map<string, WebGLProgram>();
    private gl: WebGL2RenderingContext | null = null;
    private aPos: GLint = 0; // TODO: use VAO
    private aCenter: GLint = 0; // TODO: use VAO
    private aColor: GLint = 0; // TODO: use VAO
    private aAlpha: GLint = 0; // TODO: use VAO
    private vertexBuffer: WebGLBuffer | null = null;
    private centerBuffer: WebGLBuffer | null = null;
    private colorBuffer: WebGLBuffer | null = null;
    private alphaBuffer: WebGLBuffer | null = null;
    private colorRamp: WebGLTexture | null = null;
    private map: MaplibreMap | null = null;
    private instanceCount = 0;
    private _radius: ZoomExpression = 1;
    private _opacity = 1.0;

    constructor(public readonly id: string) {}

    get opacity(): number {
        return this._opacity;
    }

    set opacity(value: number) {
        this._opacity = value;
        this.map?.triggerRepaint();
    }

    get radius() {
        return this._radius;
    }

    set radius(value: ZoomExpression) {
        this._radius = value;
        this.map?.triggerRepaint();
    }

    private getShader(gl: WebGLRenderingContext | WebGL2RenderingContext, shaderDescription: CustomRenderMethodInput['shaderData']) {
        // Pick a shader based on the current projection, defined by `variantName`.
        if (this.shaderMap.has(shaderDescription.variantName)) {
            return this.shaderMap.get(shaderDescription.variantName);
        }

        const vs = vertexSource
            .replace('__define__', shaderDescription.define)
            .replace('__prelude__', shaderDescription.vertexShaderPrelude);

        // create a vertex shader
        const vertexShader = gl.createShader(gl.VERTEX_SHADER)!;
        gl.shaderSource(vertexShader, vs);
        gl.compileShader(vertexShader);
        if (!gl.getShaderParameter(vertexShader, gl.COMPILE_STATUS)) {
            console.error(gl.getShaderInfoLog(vertexShader));
        }

        // create a fragment shader
        const fragmentShader = gl.createShader(gl.FRAGMENT_SHADER)!;
        gl.shaderSource(fragmentShader, fragmentSource);
        gl.compileShader(fragmentShader);
        if (!gl.getShaderParameter(fragmentShader, gl.COMPILE_STATUS)) {
            console.error(gl.getShaderInfoLog(fragmentShader));
        }

        // link the two shaders into a WebGL program
        const program = gl.createProgram();
        gl.attachShader(program, vertexShader);
        gl.attachShader(program, fragmentShader);
        gl.linkProgram(program);

        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
            console.error(gl.getProgramInfoLog(program));
        }

        this.aPos = gl.getAttribLocation(program, 'a_pos');
        this.aCenter = gl.getAttribLocation(program, 'a_center');
        this.aColor = gl.getAttribLocation(program, 'a_color');
        this.aAlpha = gl.getAttribLocation(program, 'a_alpha');

        this.shaderMap.set(shaderDescription.variantName, program);

        return program;
    }

    setRamp(ramp: Uint8Array) {
        if (!this.gl) {
            return;
        }

        const gl = this.gl;
        gl.bindTexture(gl.TEXTURE_2D, this.colorRamp);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, ramp.byteLength / 4, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, ramp);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    }

    setData(items: Array<{ position: number[]; color: number; alpha: number; id: number }>) {
        if (!this.gl) {
            return;
        }

        const gl = this.gl;
        gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(items.map(() => [-1, 1, 1, 1, -1, -1, 1, -1]).flat()), gl.STATIC_DRAW);

        this.instanceCount = items.length;

        gl.bindBuffer(gl.ARRAY_BUFFER, this.centerBuffer);
        gl.bufferData(
            gl.ARRAY_BUFFER,
            new Float32Array(
                items
                    .map((x) => {
                        const pos = mercatorUtils.mercatorFromLngLat(x.position);
                        return [pos[0], pos[1]];
                    })
                    .flat()
            ),
            gl.STATIC_DRAW
        );

        gl.bindBuffer(gl.ARRAY_BUFFER, this.colorBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Uint8Array(items.map((x) => x.color)), gl.STATIC_DRAW);

        gl.bindBuffer(gl.ARRAY_BUFFER, this.alphaBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Uint8Array(items.map((x) => x.alpha)), gl.STATIC_DRAW);

        this.map?.triggerRepaint();
    }

    onAdd(map: MaplibreMap, gl: WebGL2RenderingContext): void {
        this.map = map;
        this.gl = gl;
        this.vertexBuffer = gl.createBuffer();
        this.centerBuffer = gl.createBuffer();
        this.colorBuffer = gl.createBuffer();
        this.alphaBuffer = gl.createBuffer();
        this.colorRamp = gl.createTexture();
    }

    onRemove(map: MaplibreMap, gl: WebGLRenderingContext | WebGL2RenderingContext): void {
        this.map = null;
        gl.deleteBuffer(this.vertexBuffer);
        gl.deleteBuffer(this.centerBuffer);
        gl.deleteBuffer(this.colorBuffer);
        gl.deleteBuffer(this.alphaBuffer);
        gl.deleteTexture(this.colorRamp);

        gl.disableVertexAttribArray(this.aPos);
        gl.disableVertexAttribArray(this.aCenter);
        gl.disableVertexAttribArray(this.aColor);
        gl.disableVertexAttribArray(this.aAlpha);

        this.vertexBuffer = null;
        this.centerBuffer = null;
        this.colorBuffer = null;
        this.alphaBuffer = null;
        this.colorRamp = null;

        for (const program of this.shaderMap.values()) {
            gl.deleteProgram(program);
        }

        this.shaderMap.clear();
        this.gl = null;
    }

    render(_gl: WebGLRenderingContext | WebGL2RenderingContext, args: CustomRenderMethodInput) {
        if (!this.map) {
            return;
        }
        const gl = _gl as WebGL2RenderingContext;
        const globe = args.shaderData.variantName === 'globe';
        (globe ? [0] : this.getWorldOffsets()).forEach((offset) => {
            const program = this.getShader(gl, args.shaderData)!;
            gl.enable(gl.BLEND);
            gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
            gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.SRC_ALPHA, gl.DST_ALPHA);

            gl.useProgram(program);
            gl.uniformMatrix4fv(
                gl.getUniformLocation(program, 'u_projection_fallback_matrix'),
                false,
                args.defaultProjectionData.fallbackMatrix // convert mat4 from gl-matrix to a plain array
            );
            gl.uniformMatrix4fv(
                gl.getUniformLocation(program, 'u_projection_matrix'),
                false,
                args.defaultProjectionData.mainMatrix // convert mat4 from gl-matrix to a plain array
            );
            gl.uniform4f(
                gl.getUniformLocation(program, 'u_projection_tile_mercator_coords'),
                ...args.defaultProjectionData.tileMercatorCoords
            );

            gl.uniform4f(gl.getUniformLocation(program, 'u_projection_clipping_plane'), ...args.defaultProjectionData.clippingPlane);
            gl.uniform1f(gl.getUniformLocation(program, 'u_projection_transition'), args.defaultProjectionData.projectionTransition);
            gl.uniform1f(gl.getUniformLocation(program, 'u_worldXOffset'), offset);
            gl.uniform1f(gl.getUniformLocation(program, 'u_opacity'), this.opacity);
            gl.uniform1f(gl.getUniformLocation(program, 'u_outline'), this.map!.getZoom() > 3.5 ? 1 : 0);
            const ptu = this.map!._camera.transform.pixelsToGLUnits;
            const radius = typeof this._radius === 'function' ? this._radius(this.map!.getZoom()) : this._radius;
            gl.uniform2f(gl.getUniformLocation(program, 'u_radius'), ptu[0] * radius, ptu[1] * radius);

            gl.bindBuffer(gl.ARRAY_BUFFER, this.colorBuffer);
            gl.enableVertexAttribArray(this.aColor);
            gl.vertexAttribPointer(this.aColor, 1, gl.UNSIGNED_BYTE, false, 0, 0);
            gl.vertexAttribDivisor(this.aColor, 1);

            gl.activeTexture(gl.TEXTURE0);
            gl.bindTexture(gl.TEXTURE_2D, this.colorRamp);
            gl.uniform1i(gl.getUniformLocation(program, 'u_ramp'), 0);

            gl.bindBuffer(gl.ARRAY_BUFFER, this.centerBuffer);
            gl.enableVertexAttribArray(this.aCenter);
            gl.vertexAttribPointer(this.aCenter, 2, gl.FLOAT, false, 0, 0);
            gl.vertexAttribDivisor(this.aCenter, 1);

            gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexBuffer);
            gl.enableVertexAttribArray(this.aPos);
            gl.vertexAttribPointer(this.aPos, 2, gl.FLOAT, false, 0, 0);

            gl.bindBuffer(gl.ARRAY_BUFFER, this.alphaBuffer);
            gl.enableVertexAttribArray(this.aAlpha);
            gl.vertexAttribPointer(this.aAlpha, 1, gl.UNSIGNED_BYTE, false, 0, 0);
            gl.vertexAttribDivisor(this.aAlpha, 1);

            gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, this.instanceCount);
        });
        // IMPORTANT: reset divisors back to 0 otherwise this will affect other shader programs that run after this one
        gl.vertexAttribDivisor(this.aColor, 0);
        gl.vertexAttribDivisor(this.aCenter, 0);
        gl.vertexAttribDivisor(this.aAlpha, 0);
    }

    private getWorldOffsets() {
        const bounds = this.map!.getBounds();
        const west = bounds.getWest();
        const east = bounds.getEast();

        const x1 = 0 - Math.ceil((Math.abs(west) - 180) / 360);
        const x2 = Math.ceil((Math.abs(east) - 180) / 360);

        const worldOffsets = [];
        for (let i = x1; i <= x2; i++) {
            worldOffsets.push(i);
        }
        return worldOffsets;
    }
}
